import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Landmark, ArrowRight, Save, RotateCcw, Loader2, Trash2, History } from "lucide-react";
import { wealthApi } from "@/api/wealth";

// BANK BEAT ENGINE — isolated mortgage-optimisation macro for the Wealth surface.
// Self-contained: local state + native daily-compounding amortization. Neo-Brutalist
// styling matches WealthMonitor (border-2 border-black, flat offset shadows, mono).

// Primary AU banks -> offset products + 55-day interest-free cards (indicative rates).
const BANKS = {
  CBA: { name: "CommBank", rate: 6.24, offset: "MISA 100% Offset", card: "CommBank Awards (up to 55 days interest-free)" },
  Westpac: { name: "Westpac", rate: 6.19, offset: "Rocket Repay Offset", card: "Low Rate Card (up to 55 days)" },
  ANZ: { name: "ANZ", rate: 6.14, offset: "ANZ Offset Account", card: "ANZ Platinum (up to 55 days)" },
  NAB: { name: "NAB", rate: 6.29, offset: "NAB Base Variable + Offset", card: "NAB Rewards (up to 55 days)" },
  Macquarie: { name: "Macquarie", rate: 5.99, offset: "Offset Home Loan", card: "Macquarie Black (up to 55 days)" },
  ING: { name: "ING", rate: 6.09, offset: "Orange Advantage Offset", card: "Orange One (up to 55 days)" },
};

const money = (v) => `$${Math.round(Number(v) || 0).toLocaleString()}`;

// Native amortization — daily-compounded interest on the (balance - offset) balance.
function simulate({ loan, offset, ratePct, years = 30 }) {
  const dailyRate = ratePct / 100 / 365;
  const monthlyRate = ratePct / 100 / 12;
  const n = years * 12;
  const repayment = (loan * monthlyRate) / (1 - Math.pow(1 + monthlyRate, -n));
  const run = (offsetBal) => {
    let bal = loan, months = 0, interestPaid = 0;
    while (bal > 0 && months < n) {
      const eff = Math.max(0, bal - offsetBal);
      const interest = eff * dailyRate * 30; // ~30 days/month, daily-compounded
      if (repayment <= interest && offsetBal <= 0) { months = n; break; }
      interestPaid += interest;
      bal = bal + interest - repayment;
      months++;
    }
    return { months: Math.min(months, n), interestPaid };
  };
  const base = run(0);
  const opt = run(Math.max(0, offset));
  return {
    repayment,
    baseYears: base.months / 12,
    optYears: opt.months / 12,
    yearsSaved: Math.max(0, (base.months - opt.months) / 12),
    cashKept: Math.max(0, base.interestPaid - opt.interestPaid),
  };
}

const num = (s) => Number(String(s).replace(/[^0-9.]/g, "")) || 0;
const btn = "flex items-center justify-center gap-1.5 border-2 border-black font-mono text-[11px] font-black uppercase tracking-widest shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none disabled:opacity-40";
const inputCls = "w-full border-2 border-black bg-white px-2.5 py-2 font-mono text-sm font-bold text-black outline-none focus:bg-[#FFF9E6]";

export default function BankBeatEngine({ onSaved }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ bank: "CBA", loan: "650000", deposit: "130000", savings: "45000" });
  const [res, setRes] = useState(null);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [histLoading, setHistLoading] = useState(true);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const bank = BANKS[form.bank];

  const loadHistory = async () => {
    try {
      const r = await wealthApi.listBlueprints();
      setHistory(r.blueprints || []);
    } catch { /* non-blocking — empty history */ }
    finally { setHistLoading(false); }
  };
  useEffect(() => { loadHistory(); }, []);

  const calc = () => {
    const loan = num(form.loan);
    if (loan <= 0) { toast.error("Enter your loan amount"); return; }
    const savings = num(form.savings);
    // 55-day card lets ~1 month of spend sit in the offset -> small extra offset boost.
    const cardBoost = Math.min(savings * 0.15, 8000);
    setRes(simulate({ loan, offset: savings + cardBoost, ratePct: bank.rate }));
    setStep(2);
  };

  const saveBlueprint = async () => {
    if (!res || saving) return;
    setSaving(true);
    try {
      // Titles are handled automatically by the backend ("My <Bank> Blueprint").
      const payload = {
        target_lender: form.bank,
        property_value: num(form.loan) + num(form.deposit),
        deposit_amount: num(form.deposit),
        savings_buffer: num(form.savings),
        years_saved: res.yearsSaved,
        cash_kept: res.cashKept,
      };
      const r = await wealthApi.saveBlueprint(payload);
      setHistory((h) => [r.blueprint, ...h]);
      onSaved?.(r.blueprint);
      toast.success("Strategy activated & saved ✓");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed — try again");
    } finally { setSaving(false); }
  };

  const removeBlueprint = async (id) => {
    try {
      await wealthApi.deleteBlueprint(id);
      setHistory((h) => h.filter((x) => x.id !== id));
      toast("Blueprint removed");
    } catch { toast.error("Delete failed"); }
  };

  const reset = () => { setRes(null); setStep(1); };

  return (
    <div className="space-y-3" data-testid="bankbeat-macro">
    <div className="border-2 border-black bg-white shadow-[4px_4px_0px_#000]" data-testid="bankbeat-engine">
      {/* header */}
      <div className="flex items-center gap-2 border-b-2 border-black bg-[#FFCC00] px-3 py-2">
        <Landmark size={15} strokeWidth={2.6} className="text-black" />
        <p className="font-mono text-[11px] font-black uppercase tracking-widest text-black">Mortgage Blueprint</p>
        <span className="ml-auto font-mono text-[9px] font-black text-black/60">STEP {step}/2</span>
      </div>

      <div className="p-3">
        {/* STEP 1 — Bank & Numbers */}
        {step === 1 && (
          <div className="space-y-3" data-testid="bankbeat-step-1">
            <div>
              <label className="font-mono text-[9px] font-black uppercase tracking-widest text-black/60">Your Bank</label>
              <div className="mt-1 grid grid-cols-3 gap-1.5">
                {Object.entries(BANKS).map(([k, b]) => (
                  <button key={k} data-testid={`bankbeat-bank-${k}`} onClick={() => setForm((f) => ({ ...f, bank: k }))}
                    className={`border-2 border-black py-1.5 font-mono text-[10px] font-black uppercase ${form.bank === k ? "bg-black text-[#FFCC00]" : "bg-white text-black"}`}>
                    {b.name}
                  </button>
                ))}
              </div>
            </div>
            {[["loan", "Loan Amount"], ["deposit", "Deposit"], ["savings", "Savings in Offset"]].map(([k, label]) => (
              <div key={k}>
                <label className="font-mono text-[9px] font-black uppercase tracking-widest text-black/60">{label}</label>
                <input data-testid={`bankbeat-input-${k}`} value={form[k]} onChange={set(k)} inputMode="numeric" placeholder="$" className={`mt-1 ${inputCls}`} />
              </div>
            ))}
            <button data-testid="bankbeat-calc" onClick={calc} className={`${btn} w-full bg-[#98FFB6] text-black py-3`}>
              Run Blueprint <ArrowRight size={14} strokeWidth={3} />
            </button>
          </div>
        )}

        {/* STEP 2 — Dead-simple, high-contrast payoff + one action */}
        {step === 2 && res && (
          <div className="space-y-3" data-testid="bankbeat-step-2">
            {/* 💰 Cash — huge & green */}
            <div className="border-2 border-black bg-[#98FFB6] shadow-[3px_3px_0px_#000] px-3 py-5 text-center">
              <p className="font-mono text-[10px] font-black uppercase tracking-widest text-black/70">💰 Cash Put Back In Your Pocket</p>
              <p data-testid="bankbeat-cash-kept" className="mt-1.5 font-mono text-4xl font-black leading-none text-black">{money(res.cashKept)}</p>
            </div>
            {/* ⏳ Years — huge & clear */}
            <div className="border-2 border-black bg-black shadow-[3px_3px_0px_#000] px-3 py-5 text-center">
              <p className="font-mono text-[10px] font-black uppercase tracking-widest text-[#FFCC00]">⏳ Years Cut Off Your Mortgage</p>
              <p data-testid="bankbeat-years-saved" className="mt-1.5 font-mono text-4xl font-black leading-none text-white">{res.yearsSaved.toFixed(1)} <span className="text-xl">Years</span></p>
            </div>
            <button data-testid="bankbeat-save" onClick={saveBlueprint} disabled={saving} className={`${btn} w-full bg-[#FFCC00] text-black py-4`}>
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} strokeWidth={3} />} {saving ? "Saving…" : "Activate & Save This Strategy"}
            </button>
            <button onClick={reset} data-testid="bankbeat-reset" className={`${btn} w-full bg-white text-black py-2`}>
              <RotateCcw size={12} strokeWidth={3} /> Start Over
            </button>
          </div>
        )}
      </div>
    </div>

    {/* SAVED HISTORY — persisted mortgage scenarios (MongoDB-backed) */}
    <div className="border-2 border-black bg-white shadow-[4px_4px_0px_#000]" data-testid="bankbeat-history">
      <div className="flex items-center gap-2 border-b-2 border-black bg-black px-3 py-2">
        <History size={14} strokeWidth={2.6} className="text-[#FFCC00]" />
        <p className="font-mono text-[11px] font-black uppercase tracking-widest text-[#FFCC00]">Saved Blueprints</p>
        <span className="ml-auto font-mono text-[9px] font-black text-white/60" data-testid="bankbeat-history-count">{history.length}</span>
      </div>
      <div className="p-3 space-y-2">
        {histLoading ? (
          <div className="grid place-items-center py-4" data-testid="bankbeat-history-loading">
            <Loader2 size={18} className="animate-spin text-black/60" />
          </div>
        ) : history.length === 0 ? (
          <p className="font-mono text-[10px] uppercase tracking-widest text-black/45 py-2 text-center" data-testid="bankbeat-history-empty">
            No blueprints saved yet · run one above
          </p>
        ) : (
          history.map((b) => (
            <div key={b.id} data-testid={`bankbeat-history-card-${b.id}`} className="flex items-center gap-2.5 border-2 border-black bg-[#FFF9E6] shadow-[2px_2px_0px_#000] px-3 py-2.5">
              <Landmark size={15} strokeWidth={2.6} className="text-black shrink-0" />
              <p data-testid={`bankbeat-history-summary-${b.id}`} className="flex-1 min-w-0 font-mono text-[11px] font-bold text-black leading-snug">
                Your <b>{(BANKS[b.target_lender] && BANKS[b.target_lender].name) || b.target_lender}</b> Blueprint saves you <b className="text-emerald-700">{money(b.cash_kept)}</b>.
              </p>
              <button data-testid={`bankbeat-history-delete-${b.id}`} onClick={() => removeBlueprint(b.id)} aria-label="Delete blueprint"
                className="grid h-8 w-8 shrink-0 place-items-center border-2 border-black bg-[#FF6B6B] text-black shadow-[2px_2px_0px_#000] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none">
                <Trash2 size={13} strokeWidth={2.6} />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
    </div>
  );
}
